import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { trackEvent, SQUARE_MONTHLY_URL, SQUARE_ANNUAL_URL } from "@/lib/api";

const FEATURES = [
  "The full Wired2Win Interpretive Guide — your profile, decoded in depth",
  "Practice Par forms to structure every range session",
  "Golf Round Analysis scorecards that expose your real patterns",
  "The weekly wiring drill, plus every new W2W asset as it drops",
];

const PlanCard = ({ name, price, per, blurb, url, featured, testid, delay }) => {
  const go = () => {
    trackEvent("membership_click", { plan: name });
    if (url) {
      window.location.href = url;
    } else {
      toast.info("Checkout is being connected — join the newsletter below and we'll email you the moment membership opens.");
    }
  };

  return (
    <Reveal delay={delay}>
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ duration: 0.25 }}
        data-testid={testid}
        className={`relative border p-8 sm:p-10 h-full flex flex-col ${
          featured ? "border-volt bg-volt/5" : "border-white/10 bg-ink"
        }`}
      >
        {featured && (
          <span className="absolute -top-3.5 left-8 bg-volt text-obsidian text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
            Best value — save 34%
          </span>
        )}
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-white/40">{name}</p>
        <div className="flex items-baseline gap-2 mt-4">
          <span className="font-display font-black tracking-tighter text-5xl sm:text-6xl">{price}</span>
          <span className="text-white/50 text-sm font-semibold">{per}</span>
        </div>
        <p className="text-white/50 text-sm leading-relaxed mt-4 mb-8">{blurb}</p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={go}
          data-testid={`${testid}-button`}
          className={`group mt-auto w-full inline-flex items-center justify-center gap-2 font-display font-bold px-6 py-4 transition-colors duration-200 ${
            featured
              ? "bg-volt text-obsidian hover:bg-white"
              : "border border-white/25 text-white hover:border-volt hover:text-volt"
          }`}
        >
          Choose {name}
          <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
        </motion.button>
      </motion.div>
    </Reveal>
  );
};

export const Membership = () => (
  <section className="border-t border-white/10 bg-ink/40" data-testid="membership-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Overline>Wired2Win Membership</Overline>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl uppercase mt-4">
            Go deeper than <span className="text-volt">the report.</span>
          </h2>
          <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed max-w-md">
            The assessment and report are free — they show you the pattern. Membership hands you the tools to rewrite it.
          </p>
          <ul className="mt-8 space-y-4">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/70" data-testid={`membership-feature-${i}`}>
                <Check className="w-4 h-4 text-volt shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-6 content-center">
          <PlanCard
            name="Monthly"
            price="$9.99"
            per="/month"
            blurb="Full access to every Wired2Win training asset. Cancel anytime."
            url={SQUARE_MONTHLY_URL}
            testid="membership-monthly"
            delay={0.1}
          />
          <PlanCard
            name="Annual"
            price="$79"
            per="/year"
            blurb="Everything in Monthly, at two months free. One decision, all season."
            url={SQUARE_ANNUAL_URL}
            featured
            testid="membership-annual"
            delay={0.2}
          />
        </div>
        <Reveal delay={0.1}>
          <p className="mt-12 text-sm text-white/40">
            Are you a golf coach?{" "}
            <Link to="/coaches" data-testid="membership-coaches-link" className="text-volt font-semibold hover:text-white transition-colors">
              Learn about the certified coach program
            </Link>
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
